<template>
  <b-modal
    v-model="localVisible"
    title="Manage Beacon Configurations"
    size="xl"
    hide-footer
    lazy
  >
    <!-- Add/Edit Form -->
    <b-card v-if="showConfigForm" class="mb-3">
      <b-card-header>
        <h4 class="mb-0">{{ editMode ? 'Edit Beacon' : 'Add New Beacon' }}</h4>
      </b-card-header>
      <b-card-body>
        <b-form>
          <b-row>
            <b-col md="6">
              <b-form-group label="Beacon MAC Address" label-for="beacon-mac">
                <b-form-input
                  id="beacon-mac"
                  v-model="configForm.beacon_mac"
                  :disabled="editMode"
                  placeholder="ac233f2f8f10"
                  required
                ></b-form-input>
                <small class="text-muted">Format: lowercase hex without colons</small>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Patient Name" label-for="patient-name">
                <b-form-input
                  id="patient-name"
                  v-model="configForm.patient_name"
                  placeholder="John Doe"
                  required
                ></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="6">
              <b-form-group label="Room Number" label-for="room-number">
                <b-form-input
                  id="room-number"
                  v-model="configForm.room_number"
                  placeholder="Room 101"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="RSSI Threshold (Signal Strength)">
                <b-row>
                  <b-col md="8">
                    <b-form-input
                      v-model="configForm.rssi_threshold"
                      type="range"
                      min="-100"
                      max="-40"
                      step="5"
                    ></b-form-input>
                  </b-col>
                  <b-col md="4">
                    <b-form-input
                      v-model="configForm.rssi_threshold"
                      type="number"
                      readonly
                    ></b-form-input>
                  </b-col>
                </b-row>
                <small class="text-muted">{{ configForm.rssi_threshold }} dBm (recommended: -75)</small>
              </b-form-group>
            </b-col>
          </b-row>

          <!-- Medications -->
          <b-form-group label="Medications">
            <div v-for="(med, index) in configForm.medications" :key="index" class="mb-3 p-3 border rounded">
              <b-row>
                <b-col md="4">
                  <b-form-input
                    v-model="med.name"
                    placeholder="Medication name"
                    required
                  ></b-form-input>
                </b-col>
                <b-col md="3">
                  <b-form-input
                    v-model="med.dosage"
                    placeholder="Dosage (e.g., 500mg)"
                    required
                  ></b-form-input>
                </b-col>
                <b-col md="3">
                  <b-form-input
                    v-model="med.time"
                    type="time"
                    required
                  ></b-form-input>
                </b-col>
                <b-col md="2">
                  <b-button
                    variant="danger"
                    size="sm"
                    @click="$emit('remove-medication', index)"
                    :disabled="configForm.medications.length === 1"
                    block
                  >
                    <i class="fas fa-trash"></i>
                  </b-button>
                </b-col>
              </b-row>
            </div>
            <b-button
              variant="outline-primary"
              size="sm"
              @click="$emit('add-medication')"
              :disabled="configForm.medications.length >= 5"
            >
              <i class="fas fa-plus"></i> Add Medication
            </b-button>
          </b-form-group>

          <!-- Active Status -->
          <b-form-group>
            <b-form-checkbox v-model="configForm.active" switch size="lg">
              {{ configForm.active ? 'Active' : 'Inactive' }}
            </b-form-checkbox>
          </b-form-group>

          <!-- Notes -->
          <b-form-group label="Notes" label-for="notes">
            <b-form-textarea
              id="notes"
              v-model="configForm.notes"
              placeholder="Additional notes..."
              rows="2"
            ></b-form-textarea>
          </b-form-group>

          <!-- Form Actions -->
          <b-row>
            <b-col>
              <b-button variant="secondary" @click="$emit('cancel-form')">
                Cancel
              </b-button>
              <b-button variant="primary" @click="$emit('save-beacon')" :disabled="!isFormValid" class="ml-2">
                {{ editMode ? 'Update' : 'Create' }}
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </b-card-body>
    </b-card>

    <!-- Beacon List -->
    <b-card>
      <b-card-header>
        <b-row align-v="center">
          <b-col>
            <h4 class="mb-0">Configured Beacons ({{ configuredBeacons.length }})</h4>
          </b-col>
          <b-col class="text-right">
            <b-button variant="primary" size="sm" @click="$emit('add-beacon')">
              <i class="fas fa-plus"></i> Add Beacon
            </b-button>
          </b-col>
        </b-row>
      </b-card-header>
      
      <!-- Loading State -->
      <div v-if="loadingConfigs" class="text-center p-4">
        <b-spinner variant="primary"></b-spinner>
        <div>Loading beacons...</div>
      </div>

      <!-- Empty State -->
      <div v-else-if="configuredBeacons.length === 0" class="text-center p-4">
        <i class="fas fa-bluetooth fa-2x text-muted mb-2"></i>
        <p class="text-muted">No configured beacons</p>
      </div>

      <!-- Bootstrap Table -->
      <div v-else class="table-responsive">
        <table class="table table-hover table-sm">
          <thead class="thead-light">
            <tr>
              <th>MAC</th>
              <th>Patient</th>
              <th>Room</th>
              <th>Medications</th>
              <th>Status</th>
              <th>RSSI</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="beacon in configuredBeacons" :key="beacon.id">
              <td><strong>{{ beacon.beacon_mac }}</strong></td>
              <td>{{ beacon.patient_name }}</td>
              <td>{{ beacon.room_number || 'N/A' }}</td>
              <td>
                <b-badge
                  v-for="(med, idx) in beacon.medications"
                  :key="idx"
                  variant="info"
                  class="mr-1 mb-1"
                >
                  {{ med.formatted || `${med.name} ${med.dosage} ${med.time}` }}
                </b-badge>
                <span v-if="!beacon.medications || beacon.medications.length === 0" class="text-muted">None</span>
              </td>
              <td>
                <b-badge :variant="beacon.active ? 'success' : 'secondary'">
                  {{ beacon.active ? 'Active' : 'Inactive' }}
                </b-badge>
              </td>
              <td>{{ beacon.rssi_threshold }} dBm</td>
              <td>
                <b-button size="sm" variant="info" @click="$emit('edit-beacon', beacon)" class="mr-1">
                  <i class="fas fa-edit"></i>
                </b-button>
                <b-button size="sm" variant="danger" @click="$emit('delete-beacon', beacon)">
                  <i class="fas fa-trash"></i>
                </b-button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Original Element Table (Commented out) -->
      <!--
      <el-table
        :data="configuredBeacons"
        header-row-class-name="thead-light"
        v-loading="loadingConfigs"
      >
        <el-table-column label="MAC" prop="beacon_mac" width="150" />
        <el-table-column label="Patient" prop="patient_name" width="180" />
        <el-table-column label="Room" prop="room_number" width="120" />
        <el-table-column label="Medications" min-width="250">
          <template v-slot="{ row }">
            <b-badge
              v-for="(med, idx) in row.medications"
              :key="idx"
              variant="info"
              class="mr-1 mb-1"
            >
              {{ med.formatted }}
            </b-badge>
          </template>
        </el-table-column>
        <el-table-column label="Status" width="100">
          <template v-slot="{ row }">
            <b-badge :variant="row.active ? 'success' : 'secondary'">
              {{ row.active ? 'Active' : 'Inactive' }}
            </b-badge>
          </template>
        </el-table-column>
        <el-table-column label="RSSI" width="100">
          <template v-slot="{ row }">
            {{ row.rssi_threshold }} dBm
          </template>
        </el-table-column>
        <el-table-column label="Actions" width="150">
          <template v-slot="{ row }">
            <b-button size="sm" variant="info" @click="$emit('edit-beacon', row)" class="mr-1">
              <i class="fas fa-edit"></i>
            </b-button>
            <b-button size="sm" variant="danger" @click="$emit('delete-beacon', row)">
              <i class="fas fa-trash"></i>
            </b-button>
          </template>
        </el-table-column>
      </el-table>
      -->
    </b-card>
  </b-modal>
</template>

<script>
export default {
  name: 'BeaconConfigModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    showConfigForm: {
      type: Boolean,
      default: false
    },
    editMode: {
      type: Boolean,
      default: false
    },
    configuredBeacons: {
      type: Array,
      default: () => []
    },
    loadingConfigs: {
      type: Boolean,
      default: false
    },
    configForm: {
      type: Object,
      default: () => ({
        beacon_mac: '',
        patient_name: '',
        room_number: '',
        medications: [{ name: '', dosage: '', time: '08:00' }],
        rssi_threshold: -75,
        active: true,
        notes: ''
      })
    }
  },
  computed: {
    // Two-way binding for visible prop
    localVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit('update:visible', value);
      }
    },
    
    isFormValid() {
      return (
        this.configForm.beacon_mac &&
        this.configForm.patient_name &&
        this.configForm.medications.length > 0 &&
        this.configForm.medications.every(m => m.name && m.dosage && m.time)
      );
    }
  },
  // Remove watchers to prevent flickering
  // The parent should handle all data management
  emits: [
    'update:visible',
    'add-beacon',
    'edit-beacon',
    'save-beacon',
    'delete-beacon',
    'cancel-form',
    'add-medication',
    'remove-medication',
    'reset-form'
  ]
}
</script>

<style scoped>
.table-sm td, .table-sm th {
  padding: 0.5rem;
}

.table-hover tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.075);
}
</style>